import { useQuery } from '@apollo/client/react';
import { type OnChangeFn, type SortingState } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ActiveTab } from './ActiveTab';
import { FilterBar } from './FilterBar';
import { InactiveTab } from './InactiveTab';
import { MyDealsTab } from './MyDealsTab';
import { NewDealDialog } from './NewDealDialog';
import {
  GET_ACCOUNTS,
  GET_DEAL_OPPORTUNITIES,
  GET_EMPLOYEES_AND_DEAL_SELECTS
} from './queries';
import { TopOfFunnelTab } from './TopOfFunnelTab';
import type {
  EmployeesAndDealSelects,
  GetAccounts,
  GetDealOpportunitiesData
} from './types';

import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebounce } from '@/hooks/useDebounce';
interface InvestmentsDirectProps {
  activeDealsOnlySwitchIsChecked: boolean;
  data1?: GetDealOpportunitiesData;
  data3: GetAccounts;
  dataEmployeesAndDealSelects: EmployeesAndDealSelects;
  inactivePage: number;
  inactiveSorting: SortingState;
  loading1: boolean;
  onDealSourceSearchChange: (value: string) => void;
  onRefreshDeals: () => Promise<unknown>;
  onTabChange: (value: string) => void;
  selectedDealSource: string[];
  selectedDealTeam: string[];
  selectedNameFilter: string | undefined;
  selectedStatusFilter: string | undefined;
  setActiveDealsOnlySwitchIsChecked: (checked: boolean) => void;
  setInactivePage: (page: number) => void;
  setInactiveSorting: OnChangeFn<SortingState>;
  setSelectedDealSource: (source: string[]) => void;
  setSelectedDealTeam: (team: string[]) => void;
  setSelectedNameFilter: (filter: string | undefined) => void;
  setSelectedStatusFilter: (filter: string | undefined) => void;
  setTopOfFunnelPage: (page: number) => void;
  setTopOfFunnelSorting: OnChangeFn<SortingState>;
  tabValue: string;
  topOfFunnelPage: number;
  topOfFunnelSorting: SortingState;
}

const InvestmentsDirect = ({
  activeDealsOnlySwitchIsChecked,
  data1,
  data3,
  dataEmployeesAndDealSelects,
  inactivePage,
  inactiveSorting,
  loading1,
  onDealSourceSearchChange,
  onRefreshDeals,
  onTabChange,
  selectedDealSource,
  selectedDealTeam,
  selectedNameFilter,
  selectedStatusFilter,
  setActiveDealsOnlySwitchIsChecked,
  setInactivePage,
  setInactiveSorting,
  setSelectedDealSource,
  setSelectedDealTeam,
  setSelectedNameFilter,
  setSelectedStatusFilter,
  setTopOfFunnelPage,
  setTopOfFunnelSorting,
  tabValue,
  topOfFunnelPage,
  topOfFunnelSorting
}: InvestmentsDirectProps) => {
  return (
    <div className="flex min-h-0 grow flex-col">
      <FilterBar
        activeDealsOnlySwitchIsChecked={activeDealsOnlySwitchIsChecked}
        data3={data3}
        dataEmployeesAndDealSelects={dataEmployeesAndDealSelects}
        newDealDialogTrigger={
          <NewDealDialog
            data3={data3}
            dataEmployeesAndDealSelects={dataEmployeesAndDealSelects}
          />
        }
        onDealSourceSearchChange={onDealSourceSearchChange}
        selectedDealSource={selectedDealSource}
        selectedDealTeam={selectedDealTeam}
        selectedNameFilter={selectedNameFilter}
        selectedStatusFilter={selectedStatusFilter}
        setActiveDealsOnlySwitchIsChecked={setActiveDealsOnlySwitchIsChecked}
        setSelectedDealSource={setSelectedDealSource}
        setSelectedDealTeam={setSelectedDealTeam}
        setSelectedNameFilter={setSelectedNameFilter}
        setSelectedStatusFilter={setSelectedStatusFilter}
        tabValue={tabValue}
      />
      <div className="flex min-h-0 flex-1 flex-col px-5 py-5 md:pl-14">
        <Tabs
          className="flex flex-1 flex-col overflow-hidden"
          onValueChange={onTabChange}
          value={tabValue}
        >
          <TabsList className="w-full">
            <TabsTrigger value="my_deals">My Deals</TabsTrigger>
            <TabsTrigger value="top_of_funnel">Top of Funnel</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="inactive">Inactive</TabsTrigger>
          </TabsList>
          <TabsContent
            className="min-h-0 flex-1 overflow-hidden"
            value="my_deals"
          >
            <MyDealsTab
              dealSelects={dataEmployeesAndDealSelects}
              dealSourceFilter={selectedDealSource}
              dealTeamFilter={selectedDealTeam}
              nameFilter={selectedNameFilter}
              newDealTrigger={
                <NewDealDialog
                  data3={data3}
                  dataEmployeesAndDealSelects={dataEmployeesAndDealSelects}
                />
              }
              statusFilter={selectedStatusFilter}
            />
          </TabsContent>
          <TabsContent
            className="min-h-0 flex-1 overflow-hidden"
            value="top_of_funnel"
          >
            <TopOfFunnelTab
              currentPage={topOfFunnelPage}
              data1={data1}
              dataEmployeesAndDealSelects={dataEmployeesAndDealSelects}
              dealSelects={dataEmployeesAndDealSelects}
              loading1={loading1}
              newDealTrigger={
                <NewDealDialog
                  data3={data3}
                  dataEmployeesAndDealSelects={dataEmployeesAndDealSelects}
                />
              }
              onPageChange={setTopOfFunnelPage}
              onRefresh={onRefreshDeals}
              onSortingChange={sorting => {
                setTopOfFunnelPage(1);
                setTopOfFunnelSorting(sorting);
              }}
              sorting={topOfFunnelSorting}
            />
          </TabsContent>
          <TabsContent
            className="min-h-0 flex-1 overflow-hidden"
            value="active"
          >
            <ActiveTab
              data1={data1}
              dealSelects={dataEmployeesAndDealSelects}
              loading1={loading1}
              onRefresh={onRefreshDeals}
            />
          </TabsContent>
          <TabsContent
            className="min-h-0 flex-1 overflow-hidden"
            value="inactive"
          >
            <InactiveTab
              currentPage={inactivePage}
              data1={data1}
              loading1={loading1}
              newDealTrigger={
                <NewDealDialog
                  data3={data3}
                  dataEmployeesAndDealSelects={dataEmployeesAndDealSelects}
                />
              }
              onPageChange={setInactivePage}
              onSortingChange={sorting => {
                setInactivePage(1);
                setInactiveSorting(sorting);
              }}
              sorting={inactiveSorting}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const ITEMS_PER_PAGE = 20;
const DEFAULT_SORTING: SortingState = [{ desc: true, id: 'createDate' }];
const EMPTY_DEAL_OPPORTUNITIES_DATA: GetDealOpportunitiesData = {
  opportunities: {
    __typename: 'OpportunityNodeConnection',
    edges: [],
    pageInfo: {
      __typename: 'PageInfo',
      endCursor: null,
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: null
    },
    totalCount: 0
  }
};

const DIRECT_INVESTMENTS_ORDER_MAP: Record<string, string> = {
  createDate: 'create_date',
  killedDate: 'killed_date',
  ltmEbitda: 'ltm_ebitda',
  name: 'name',
  projectName: 'project_name',
  referencePoint: 'reference_point',
  status: 'status',
  totalProposedDebtInvestment: 'total_proposed_debt_investment',
  totalProposedEquityInvestment: 'total_proposed_equity_investment'
};

function getDirectInvestmentsOrder(sorting: SortingState) {
  const primarySort = sorting.at(0);
  if (!primarySort) {
    return 'create_date desc';
  }

  const backendField = DIRECT_INVESTMENTS_ORDER_MAP[primarySort.id];
  if (!backendField) {
    return 'create_date desc';
  }

  return `${backendField} ${primarySort.desc ? 'desc' : 'asc'}`;
}

const InvestmentsDirectContainer = () => {
  const [activeDealsOnlySwitchIsChecked, setActiveDealsOnlySwitchIsChecked] =
    useState(false);
  const [selectedDealTeam, setSelectedDealTeam] = useState<string[]>([]);
  const [tabValue, setTabValue] = useState('my_deals');
  const [selectedNameFilter, setSelectedNameFilter] = useState<
    string | undefined
  >(undefined);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<
    string | undefined
  >(undefined);
  const [selectedDealSource, setSelectedDealSource] = useState<string[]>([]);
  const [dealSourceSearch, setDealSourceSearch] = useState('');
  const debouncedDealSourceSearch = useDebounce(dealSourceSearch, 300);
  const [topOfFunnelPage, setTopOfFunnelPage] = useState(1);
  const [inactivePage, setInactivePage] = useState(1);
  const [topOfFunnelSorting, setTopOfFunnelSorting] =
    useState<SortingState>(DEFAULT_SORTING);
  const [inactiveSorting, setInactiveSorting] =
    useState<SortingState>(DEFAULT_SORTING);
  const {
    data: employeesAndDealSelectsData,
    error: employeesAndDealSelectsError,
    loading: employeesAndDealSelectsLoading
  } = useQuery<EmployeesAndDealSelects>(GET_EMPLOYEES_AND_DEAL_SELECTS);

  // Reset pagination when filters change (but not tab, as that's handled separately)
  const prevFiltersRef = useRef({
    activeDealsOnlySwitchIsChecked,
    selectedDealSource,
    selectedDealTeam,
    selectedNameFilter,
    selectedStatusFilter
  });

  useEffect(() => {
    const prev = prevFiltersRef.current;
    const hasFilterChanged =
      JSON.stringify(prev.selectedDealTeam) !==
        JSON.stringify(selectedDealTeam) ||
      prev.selectedNameFilter !== selectedNameFilter ||
      prev.selectedStatusFilter !== selectedStatusFilter ||
      JSON.stringify(prev.selectedDealSource) !==
        JSON.stringify(selectedDealSource) ||
      prev.activeDealsOnlySwitchIsChecked !== activeDealsOnlySwitchIsChecked;

    if (hasFilterChanged) {
      // Use setTimeout to avoid calling setState synchronously in effect
      setTimeout(() => {
        setTopOfFunnelPage(1);
        setInactivePage(1);
      }, 0);
      prevFiltersRef.current = {
        activeDealsOnlySwitchIsChecked,
        selectedDealSource,
        selectedDealTeam,
        selectedNameFilter,
        selectedStatusFilter
      };
    }
  }, [
    selectedDealTeam,
    selectedNameFilter,
    selectedStatusFilter,
    selectedDealSource,
    activeDealsOnlySwitchIsChecked
  ]);

  const handleTabChange = (value: string) => {
    setTabValue(value);
    // Reset pagination when tab changes
    setTopOfFunnelPage(1);
    setInactivePage(1);
  };

  const activeSortingForTab = useMemo(() => {
    if (tabValue === 'inactive') {
      return inactiveSorting;
    }
    if (tabValue === 'active') return DEFAULT_SORTING;
    return topOfFunnelSorting;
  }, [inactiveSorting, tabValue, topOfFunnelSorting]);

  // Calculate offset based on current tab and page
  const getOffset = () => {
    if (tabValue === 'my_deals' || tabValue === 'top_of_funnel') {
      return (topOfFunnelPage - 1) * ITEMS_PER_PAGE;
    }
    if (tabValue === 'inactive') {
      return (inactivePage - 1) * ITEMS_PER_PAGE;
    }
    return 0;
  };

  const {
    data: data1,
    error: error1,
    loading: loading1,
    previousData: previousData1,
    refetch: refetchDeals
  } = useQuery<GetDealOpportunitiesData>(GET_DEAL_OPPORTUNITIES, {
    fetchPolicy: 'cache-and-network',
    skip: tabValue === 'my_deals',
    variables: {
      deal_team: selectedDealTeam,
      first: ['inactive', 'top_of_funnel'].includes(tabValue)
        ? ITEMS_PER_PAGE
        : null,
      name: selectedNameFilter ?? '',
      offset: ['inactive', 'top_of_funnel'].includes(tabValue)
        ? getOffset()
        : 0,
      only_active_opportunities: activeDealsOnlySwitchIsChecked,
      order: getDirectInvestmentsOrder(activeSortingForTab),
      phase: [],
      source_ids: selectedDealSource,
      stage: [tabValue],
      status: selectedStatusFilter ?? ''
    }
  });
  const dealsData = data1 ?? previousData1 ?? EMPTY_DEAL_OPPORTUNITIES_DATA;

  const {
    data: data3,
    error: error3,
    loading: loading3
  } = useQuery<GetAccounts>(GET_ACCOUNTS, {
    variables: {
      name: debouncedDealSourceSearch,
      selectedAccounts: ''
    }
  });

  const fetchedAccounts = useMemo(() => {
    if (loading3) return { searchAccounts: data3?.searchAccounts ?? [] };
    return { searchAccounts: data3?.searchAccounts ?? [] };
  }, [data3, loading3]);

  if (employeesAndDealSelectsLoading) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error1 || employeesAndDealSelectsError || error3) {
    return <div>{'Error'}</div>;
  }

  return (
    <InvestmentsDirect
      activeDealsOnlySwitchIsChecked={activeDealsOnlySwitchIsChecked}
      data1={dealsData}
      data3={fetchedAccounts}
      /* eslint-disable @typescript-eslint/no-non-null-assertion */
      dataEmployeesAndDealSelects={employeesAndDealSelectsData!}
      /* eslint-enable @typescript-eslint/no-non-null-assertion */
      inactivePage={inactivePage}
      inactiveSorting={inactiveSorting}
      loading1={loading1}
      onDealSourceSearchChange={setDealSourceSearch}
      onRefreshDeals={refetchDeals}
      onTabChange={handleTabChange}
      selectedDealSource={selectedDealSource}
      selectedDealTeam={selectedDealTeam}
      selectedNameFilter={selectedNameFilter}
      selectedStatusFilter={selectedStatusFilter}
      setActiveDealsOnlySwitchIsChecked={setActiveDealsOnlySwitchIsChecked}
      setInactivePage={setInactivePage}
      setInactiveSorting={setInactiveSorting}
      setSelectedDealSource={setSelectedDealSource}
      setSelectedDealTeam={setSelectedDealTeam}
      setSelectedNameFilter={setSelectedNameFilter}
      setSelectedStatusFilter={setSelectedStatusFilter}
      setTopOfFunnelPage={setTopOfFunnelPage}
      setTopOfFunnelSorting={setTopOfFunnelSorting}
      tabValue={tabValue}
      topOfFunnelPage={topOfFunnelPage}
      topOfFunnelSorting={topOfFunnelSorting}
    />
  );
};

export default InvestmentsDirectContainer;
